import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { toast } from "react-toastify";
import AppDialog from "../../Components/Dialogs/AppDialog/AppDialog";
import { DashlyDropdown, DashlyMultiSelect } from "../../Components/inputs";
import ReferenceModel from "../../Models/ReferenceModel";
import { RESOURCE_LOCATION } from "../../Constants/resourceLocations";
import { AlertService } from "../../Services/AlertService";
import { DashboardService } from "../../Services/DashboardService";
import { DataserviceService } from "../../Services/DataserviceService";
import { DatasourceService } from "../../Services/DatasourceService";
import { IArticleRelation, KnowledgeArticleService } from "../../Services/KnowledgeArticleService";
import { IKnowledgeArticleSectionModel } from "../../Services/KnowledgeArticleSectionService";
import { ObjectiveService } from "../../Services/ObjectiveService";
import { ProcessflowService } from "../../Services/ProcessflowService";
import { ProjectService } from "../../Services/ProjectService";
import { resolveApiErrorMessage } from "../../Util/apiErrorUtil";
import {
    KNOWLEDGE_ARTICLE_RELATION_DISPLAY,
    KNOWLEDGE_ARTICLE_RELATION_OVERVIEW,
    KNOWLEDGE_ARTICLE_RELATION_TYPES,
    KnowledgeArticleRelationDisplay,
    KnowledgeArticleRelationType,
    asRelationLocation,
    createKnowledgeArticleRelationDraftId,
    isKnowledgeArticleRelationDraft,
    isKnowledgeArticleRelationInPageAllowed,
    isKnowledgeArticleRelationOverview,
    isKnowledgeArticleRelationOverviewOnly,
    isKnowledgeArticleRelationType,
    knowledgeArticleRelationDisplayLabelKey,
    knowledgeArticleRelationLabelKey,
    normalizeKnowledgeArticleRelationDisplay,
    normalizeKnowledgeArticleRelationSections,
    resolveKnowledgeArticleRelationDisplay,
    toRelationSelectOption,
} from "./knowledgeArticleRelationConstants";
import "./ManageKnowledgeArticleRelationsDialog.scss";

interface ManageKnowledgeArticleRelationsDialogProps {
    visible: boolean;
    articleUuid: string;
    /** The article's sections, as the page already holds them: a relation can be limited to some of them. */
    sections: IKnowledgeArticleSectionModel[];
    relations: IArticleRelation[];
    onHide: () => void;
    onChanged: () => void;
}

type KnowledgeArticleRelationItemType = Exclude<KnowledgeArticleRelationType, typeof RESOURCE_LOCATION.KNOWLEDGE_ARTICLE>;

/** Each kind of item an article can be linked to is listed by that module's own select-item endpoint. */
const relationSelectService = (location: KnowledgeArticleRelationItemType) => {
    switch (location) {
        case RESOURCE_LOCATION.ALERT:
            return new AlertService();
        case RESOURCE_LOCATION.DASHBOARD:
            return new DashboardService();
        case RESOURCE_LOCATION.DATASOURCE:
            return new DatasourceService();
        case RESOURCE_LOCATION.DATASERVICE:
            return new DataserviceService();
        case RESOURCE_LOCATION.PROCESSFLOW:
            return new ProcessflowService();
        case RESOURCE_LOCATION.OBJECTIVE:
            return new ObjectiveService();
        case RESOURCE_LOCATION.PROJECT:
            return new ProjectService();
    }
};

const fetchRelationSelectItems = async (location: KnowledgeArticleRelationType): Promise<ReferenceModel[]> => {
    if (isKnowledgeArticleRelationOverviewOnly(location)) {
        return [];
    }
    const response = await relationSelectService(location as KnowledgeArticleRelationItemType).fetchSelectItems();
    return (response.data.data ?? []).filter((item: ReferenceModel) => Boolean(item.uuid));
};

const relationSignature = (rows: IArticleRelation[]): string =>
    [...rows]
        .map((row) =>
            [
                row.resourceLinkUuid,
                row.location,
                row.locationIdentifier,
                resolveKnowledgeArticleRelationDisplay(row.location, row.locationIdentifier, row.displayMode),
                normalizeKnowledgeArticleRelationSections(row.sectionIdentifiers).join(","),
            ].join("\t")
        )
        .sort()
        .join("|");

const ManageKnowledgeArticleRelationsDialog: React.FC<ManageKnowledgeArticleRelationsDialogProps> = ({
    visible,
    articleUuid,
    sections,
    relations,
    onHide,
    onChanged,
}) => {
    const { t } = useTranslation();
    const knowledgeArticleService = useMemo(() => new KnowledgeArticleService(), []);
    const originalsRef = useRef<IArticleRelation[]>([]);
    const relationsRef = useRef(relations);
    relationsRef.current = relations;
    const [drafts, setDrafts] = useState<IArticleRelation[]>([]);
    const [itemsByType, setItemsByType] = useState<Record<string, ReferenceModel[]>>({});
    const [addType, setAddType] = useState<string | null>(null);
    const [addItem, setAddItem] = useState<string | null>(null);
    const [addDisplayMode, setAddDisplayMode] = useState<KnowledgeArticleRelationDisplay>(KNOWLEDGE_ARTICLE_RELATION_DISPLAY.DIALOG);
    const [addSections, setAddSections] = useState<string[]>([]);
    const [loadingType, setLoadingType] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const typeOptions = useMemo(
        () =>
            KNOWLEDGE_ARTICLE_RELATION_TYPES.map((location) => ({
                label: t(knowledgeArticleRelationLabelKey(location)) as string,
                value: location,
            })),
        [t]
    );

    const ensureItems = async (location: string) => {
        if (!isKnowledgeArticleRelationType(location) || itemsByType[location] || loadingType === location) {
            return;
        }
        setLoadingType(location);
        try {
            const items = await fetchRelationSelectItems(location);
            setItemsByType((current) => ({ ...current, [location]: items }));
        } catch {
            toast.error(t("knowledgeBase.relations.itemsLoadError") as string);
        } finally {
            setLoadingType((current) => (current === location ? null : current));
        }
    };

    useEffect(() => {
        if (!visible) {
            return;
        }
        const snapshot = relationsRef.current.map((relation) => ({ ...relation }));
        originalsRef.current = snapshot;
        setDrafts(snapshot.map((relation) => ({ ...relation })));
        setAddType(null);
        setAddItem(null);
        setAddDisplayMode(KNOWLEDGE_ARTICLE_RELATION_DISPLAY.DIALOG);
        setAddSections([]);
        const locations = new Set(snapshot.map((relation) => relation.location).filter(isKnowledgeArticleRelationType));
        locations.forEach((location) => {
            void ensureItems(location);
        });
        // Snapshot server rows when the dialog opens; add-type loads when the user picks one.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    useEffect(() => {
        if (!visible || !addType) {
            return;
        }
        void ensureItems(addType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, addType]);

    const linkedByType = useMemo(() => {
        const grouped: Record<string, Set<string>> = {};
        drafts.forEach((relation) => {
            if (!grouped[relation.location]) {
                grouped[relation.location] = new Set();
            }
            grouped[relation.location].add(relation.locationIdentifier);
        });
        return grouped;
    }, [drafts]);

    const overviewOption = useMemo(
        () => ({
            label: t("knowledgeBase.relations.overview") as string,
            value: KNOWLEDGE_ARTICLE_RELATION_OVERVIEW,
        }),
        [t]
    );

    const displayOptions = useMemo(
        () =>
            [
                KNOWLEDGE_ARTICLE_RELATION_DISPLAY.DIALOG,
                KNOWLEDGE_ARTICLE_RELATION_DISPLAY.LINK,
            ].map((value) => ({
                label: t(knowledgeArticleRelationDisplayLabelKey(value)) as string,
                value,
            })),
        [t]
    );
    /** In-page display exists only where the target's page lists its articles; elsewhere it is always the dialog. */
    const displayOptionsFor = (location: string | null, identifier: string | null) =>
        isKnowledgeArticleRelationInPageAllowed(location, identifier) ? displayOptions : displayOptions.slice(0, 1);

    const resolveName = (location: string, identifier: string) => {
        if (isKnowledgeArticleRelationOverview(identifier)) {
            return overviewOption.label;
        }
        const item = (itemsByType[location] ?? []).find((candidate) => candidate.uuid === identifier);
        return item?.name?.trim() || item?.code || identifier;
    };

    const optionsFor = (location: string, keepIdentifier?: string) => {
        const linked = linkedByType[location] ?? new Set<string>();
        const items = itemsByType[location] ?? [];
        const options = items
            .filter((item) => {
                if (item.uuid === keepIdentifier) {
                    return true;
                }
                return !linked.has(item.uuid as string);
            })
            .map(toRelationSelectOption);
        if (keepIdentifier && !isKnowledgeArticleRelationOverview(keepIdentifier)
            && !options.some((option) => option.value === keepIdentifier)) {
            const current = drafts.find(
                (relation) => relation.location === location && relation.locationIdentifier === keepIdentifier
            );
            options.unshift({
                label: current?.locationName || keepIdentifier,
                value: keepIdentifier,
            });
        }
        if (!linked.has(KNOWLEDGE_ARTICLE_RELATION_OVERVIEW)
            || keepIdentifier === KNOWLEDGE_ARTICLE_RELATION_OVERVIEW) {
            options.unshift(overviewOption);
        }
        return options;
    };

    const addOptions = addType ? optionsFor(addType, addItem ?? undefined) : [];
    const resolvedAddIdentifier = addItem || KNOWLEDGE_ARTICLE_RELATION_OVERVIEW;
    const sectionOptions = useMemo(
        () =>
            [...sections]
                .sort((left, right) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0))
                .map((section) => ({
                    label: section.title?.trim() || section.uuid,
                    value: section.uuid,
                })),
        [sections]
    );
    const canAdd = Boolean(addType)
        && (sections.length === 0 || addSections.length > 0)
        && !saving
        && !(linkedByType[addType ?? ""]?.has(resolvedAddIdentifier));
    const dirty = relationSignature(drafts) !== relationSignature(originalsRef.current);
    const typeFieldLabel = t("knowledgeBase.relations.addType") as string;
    const itemFieldLabel = t("knowledgeBase.relations.addItem") as string;
    const displayFieldLabel = t("knowledgeBase.relations.display") as string;
    const sectionsFieldLabel = t("knowledgeBase.relations.sections") as string;
    const sectionsSelectedLabel = t("knowledgeBase.relations.sectionsSelected", {
        defaultValue: "{0} secties",
    }) as string;
    const sectionsAllLabel = t("knowledgeBase.relations.sectionsAll") as string;
    const sectionsPlaceholder = t("knowledgeBase.relations.sectionsPlaceholder") as string;

    const handleAdd = () => {
        if (!addType || !canAdd) {
            return;
        }
        setDrafts((current) => [
            ...current,
            {
                resourceLinkUuid: createKnowledgeArticleRelationDraftId(),
                location: addType,
                locationIdentifier: resolvedAddIdentifier,
                locationName: resolveName(addType, resolvedAddIdentifier),
                displayMode: resolveKnowledgeArticleRelationDisplay(addType, resolvedAddIdentifier, addDisplayMode),
                sectionIdentifiers: normalizeKnowledgeArticleRelationSections(addSections),
            },
        ]);
        setAddItem(KNOWLEDGE_ARTICLE_RELATION_OVERVIEW);
        setAddDisplayMode(KNOWLEDGE_ARTICLE_RELATION_DISPLAY.DIALOG);
        setAddSections([]);
    };

    const handleAddTypeChange = (nextType: string | null) => {
        setAddType(nextType);
        setAddItem(nextType ? KNOWLEDGE_ARTICLE_RELATION_OVERVIEW : null);
    };

    const handleSectionsChange = (relation: IArticleRelation, nextIdentifiers: string[] | null) => {
        const next = normalizeKnowledgeArticleRelationSections(nextIdentifiers);
        setDrafts((current) =>
            current.map((row) =>
                row.resourceLinkUuid === relation.resourceLinkUuid
                    ? { ...row, sectionIdentifiers: next }
                    : row
            )
        );
    };

    const handleChange = (relation: IArticleRelation, nextIdentifier: string | null) => {
        const next = nextIdentifier || KNOWLEDGE_ARTICLE_RELATION_OVERVIEW;
        if (next === relation.locationIdentifier) {
            return;
        }
        setDrafts((current) =>
            current.map((row) =>
                row.resourceLinkUuid === relation.resourceLinkUuid
                    ? {
                        ...row,
                        locationIdentifier: next,
                        locationName: resolveName(row.location, next),
                    }
                    : row
            )
        );
    };

    const handleDisplayModeChange = (relation: IArticleRelation, nextMode: string | null) => {
        const next = resolveKnowledgeArticleRelationDisplay(relation.location, relation.locationIdentifier, nextMode);
        if (next === resolveKnowledgeArticleRelationDisplay(relation.location, relation.locationIdentifier, relation.displayMode)) {
            return;
        }
        setDrafts((current) =>
            current.map((row) =>
                row.resourceLinkUuid === relation.resourceLinkUuid
                    ? { ...row, displayMode: next }
                    : row
            )
        );
    };

    const removeDraft = (resourceLinkUuid: string) => {
        setDrafts((current) => current.filter((row) => row.resourceLinkUuid !== resourceLinkUuid));
    };

    const handleDelete = (relation: IArticleRelation, target: HTMLElement) => {
        if (isKnowledgeArticleRelationDraft(relation.resourceLinkUuid)) {
            removeDraft(relation.resourceLinkUuid);
            return;
        }
        const label = relation.locationName || relation.locationIdentifier;
        // The same anchored popup every list page uses (rendered once, in App).
        confirmPopup({
            target,
            message: t("processflow.list.deleteConfirm", { label }) as string,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("processflow.list.deleteAccept") as string,
            rejectLabel: t("processflow.list.deleteReject") as string,
            acceptClassName: "p-button-danger",
            accept: () => removeDraft(relation.resourceLinkUuid),
        });
    };

    const handleSave = async () => {
        if (!dirty || saving) {
            return;
        }
        setSaving(true);
        try {
            const originals = originalsRef.current;
            const draftIds = new Set(drafts.map((row) => row.resourceLinkUuid));
            const unlinks: string[] = [];
            const links: IArticleRelation[] = [];

            originals.forEach((original) => {
                if (!draftIds.has(original.resourceLinkUuid)) {
                    unlinks.push(original.resourceLinkUuid);
                    return;
                }
                const draft = drafts.find((row) => row.resourceLinkUuid === original.resourceLinkUuid);
                if (!draft) {
                    return;
                }
                const identifierChanged = draft.location !== original.location
                    || draft.locationIdentifier !== original.locationIdentifier;
                const modeChanged = resolveKnowledgeArticleRelationDisplay(draft.location, draft.locationIdentifier, draft.displayMode)
                    !== resolveKnowledgeArticleRelationDisplay(original.location, original.locationIdentifier, original.displayMode);
                const sectionsChanged = normalizeKnowledgeArticleRelationSections(draft.sectionIdentifiers).join(",")
                    !== normalizeKnowledgeArticleRelationSections(original.sectionIdentifiers).join(",");
                if (identifierChanged) {
                    unlinks.push(original.resourceLinkUuid);
                    links.push(draft);
                } else if (modeChanged || sectionsChanged) {
                    links.push(draft);
                }
            });
            drafts.forEach((draft) => {
                if (isKnowledgeArticleRelationDraft(draft.resourceLinkUuid)) {
                    links.push(draft);
                }
            });

            for (const resourceLinkUuid of unlinks) {
                await knowledgeArticleService.unlink(resourceLinkUuid);
            }
            for (const draft of links) {
                await knowledgeArticleService.link(
                    articleUuid,
                    asRelationLocation(draft.location),
                    draft.locationIdentifier,
                    resolveKnowledgeArticleRelationDisplay(draft.location, draft.locationIdentifier, draft.displayMode),
                    normalizeKnowledgeArticleRelationSections(draft.sectionIdentifiers)
                );
            }
            toast.success(t("common.saved") as string);
            onChanged();
            onHide();
        } catch (error) {
            toast.error(resolveApiErrorMessage(error, "knowledgeBase.relations.saveError", t));
        } finally {
            setSaving(false);
        }
    };

    return (
        <AppDialog
            visible={visible}
            onHide={onHide}
            header={t("knowledgeBase.relations.manageTitle") as string}
            className="manage-knowledge-article-relations-dialog"
            style={{ width: "min(96vw, 72rem)" }}
            footerActions={{
                cancelLabel: t("common.action.cancel") as string,
                onCancel: onHide,
                cancelDisabled: saving,
                submitLabel: t("common.action.save") as string,
                onSubmit: handleSave,
                submitDisabled: !dirty || saving,
                submitLoading: saving,
            }}
        >
            <p className="manage-knowledge-article-relations-dialog__hint">
                {t("knowledgeBase.relations.hint")}
            </p>
            <div className="manage-knowledge-article-relations-dialog__table">
            <div className="manage-knowledge-article-relations-dialog__columns">
                <span className="manage-knowledge-article-relations-dialog__column">{typeFieldLabel}</span>
                <span className="manage-knowledge-article-relations-dialog__column">{itemFieldLabel}</span>
                <span className="manage-knowledge-article-relations-dialog__column">{displayFieldLabel}</span>
                <span className="manage-knowledge-article-relations-dialog__column">{sectionsFieldLabel}</span>
                <span className="manage-knowledge-article-relations-dialog__column" />
            </div>
            <div className="manage-knowledge-article-relations-dialog__list">
                {drafts.length === 0 && (
                    <p className="manage-knowledge-article-relations-dialog__empty">
                        {t("knowledgeBase.relations.empty")}
                    </p>
                )}
                {drafts.map((relation) => {
                    const selectedSections = normalizeKnowledgeArticleRelationSections(relation.sectionIdentifiers);
                    return (
                        <div key={relation.resourceLinkUuid} className="manage-knowledge-article-relations-dialog__row">
                            <span className="manage-knowledge-article-relations-dialog__type" title={relation.location}>
                                {t(knowledgeArticleRelationLabelKey(relation.location))}
                            </span>
                            {isKnowledgeArticleRelationType(relation.location) ? (
                                <DashlyDropdown
                                    value={relation.locationIdentifier}
                                    options={optionsFor(relation.location, relation.locationIdentifier)}
                                    optionLabel="label"
                                    optionValue="value"
                                    filter
                                    showClear={false}
                                    controlSize="sm"
                                    disabled={saving}
                                    loading={loadingType === relation.location}
                                    onChange={(event) => handleChange(relation, event.value ?? null)}
                                />
                            ) : (
                                <span className="manage-knowledge-article-relations-dialog__name">
                                    {relation.locationName}
                                </span>
                            )}
                            <DashlyDropdown
                                value={resolveKnowledgeArticleRelationDisplay(
                                    relation.location, relation.locationIdentifier, relation.displayMode
                                )}
                                options={displayOptionsFor(relation.location, relation.locationIdentifier)}
                                optionLabel="label"
                                optionValue="value"
                                showClear={false}
                                controlSize="sm"
                                disabled={saving
                                    || !isKnowledgeArticleRelationInPageAllowed(relation.location, relation.locationIdentifier)}
                                aria-label={displayFieldLabel}
                                onChange={(event) => handleDisplayModeChange(relation, event.value ?? null)}
                            />
                            <DashlyMultiSelect
                                value={selectedSections}
                                options={sectionOptions}
                                optionLabel="label"
                                optionValue="value"
                                maxSelectedLabels={2}
                                selectedItemsLabel={sectionsSelectedLabel}
                                filter
                                showClear
                                controlSize="sm"
                                disabled={saving}
                                aria-label={sectionsFieldLabel}
                                placeholder={selectedSections.length === 0 ? sectionsAllLabel : sectionsPlaceholder}
                                onChange={(event) => handleSectionsChange(relation, event.value ?? [])}
                            />
                            <Button
                                type="button"
                                icon="pi pi-trash"
                                text
                                rounded
                                severity="danger"
                                className="manage-knowledge-article-relations-dialog__action"
                                aria-label={t("common.action.delete") as string}
                                disabled={saving}
                                onClick={(event) => handleDelete(relation, event.currentTarget)}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="manage-knowledge-article-relations-dialog__row manage-knowledge-article-relations-dialog__row--add">
                <DashlyDropdown
                    value={addType}
                    options={typeOptions}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={t("knowledgeBase.relations.addTypePlaceholder") as string}
                    showClear={false}
                    controlSize="sm"
                    disabled={saving}
                    aria-label={typeFieldLabel}
                    onChange={(event) => handleAddTypeChange(event.value ?? null)}
                />
                <DashlyDropdown
                    value={addItem}
                    options={addOptions}
                    optionLabel="label"
                    optionValue="value"
                    filter
                    showClear={false}
                    placeholder={t("knowledgeBase.relations.addItemPlaceholder") as string}
                    controlSize="sm"
                    disabled={saving || !addType}
                    loading={Boolean(addType && loadingType === addType)}
                    aria-label={itemFieldLabel}
                    onChange={(event) => setAddItem(event.value ?? KNOWLEDGE_ARTICLE_RELATION_OVERVIEW)}
                />
                <DashlyDropdown
                    value={resolveKnowledgeArticleRelationDisplay(addType, resolvedAddIdentifier, addDisplayMode)}
                    options={displayOptionsFor(addType, resolvedAddIdentifier)}
                    optionLabel="label"
                    optionValue="value"
                    showClear={false}
                    controlSize="sm"
                    disabled={saving || !isKnowledgeArticleRelationInPageAllowed(addType, resolvedAddIdentifier)}
                    aria-label={displayFieldLabel}
                    onChange={(event) =>
                        setAddDisplayMode(normalizeKnowledgeArticleRelationDisplay(event.value ?? null))
                    }
                />
                <DashlyMultiSelect
                    value={addSections}
                    options={sectionOptions}
                    optionLabel="label"
                    optionValue="value"
                    maxSelectedLabels={2}
                    selectedItemsLabel={sectionsSelectedLabel}
                    filter
                    showClear
                    controlSize="sm"
                    required={sections.length > 0}
                    disabled={saving || !addType}
                    aria-label={sectionsFieldLabel}
                    placeholder={sectionsPlaceholder}
                    onChange={(event) => setAddSections(normalizeKnowledgeArticleRelationSections(event.value ?? []))}
                />
                <Button
                    type="button"
                    icon="pi pi-plus"
                    text
                    rounded
                    className="manage-knowledge-article-relations-dialog__action"
                    aria-label={t("knowledgeBase.relations.add") as string}
                    tooltip={t("knowledgeBase.relations.add") as string}
                    tooltipOptions={{ position: "top" }}
                    onClick={handleAdd}
                    disabled={!canAdd}
                />
            </div>
            </div>
        </AppDialog>
    );
};

export default ManageKnowledgeArticleRelationsDialog;
